import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, onAuthStateChanged, GoogleAuthProvider, signInWithPopup, signOut, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, onSnapshot, writeBatch, connectFirestoreEmulator, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { firebaseConfig, useEmulator, emulatorConfig } from './firebase-config';
import { ChapelAttendanceSelection } from './components/chapel/ChapelAttendance';
import { ChapelAdminDashboard } from './components/chapel/ChapelAdmin';
import CommunityServiceDashboard from './components/community-service/CommunityServiceDashboard';

// --- Global Configuration ---
const CUTOFF_HOUR = 3; // Cutoff time in 24-hour format (3 AM)
const CUTOFF_BUFFER_HOURS = 24; // Buffer time in hours
const ROLES = {
    'board_paying_student': 'Board Paying',
    'non_board_paying': 'Non-Board Paying',
    'staff_or_faculty': 'Staff / Faculty',
    'chef': 'Chef',
    'admin': 'Admin'
};

// --- Firebase Initialization ---
const app = initializeApp(firebaseConfig);

const auth = getAuth(app);
const db = getFirestore(app);
const functions = getFunctions(app);

// Conditionally connect to the Firebase Local Emulator Suite
if (useEmulator) {
  console.log("Using Firebase Emulators...");
  console.log("Auth emulator:", emulatorConfig.auth);
  console.log("Firestore emulator:", emulatorConfig.firestore);
  console.log("Functions emulator:", emulatorConfig.functions);

  connectAuthEmulator(auth, `http://${emulatorConfig.auth}`);
  connectFirestoreEmulator(db, ...emulatorConfig.firestore.split(':'));
  connectFunctionsEmulator(functions, ...emulatorConfig.functions.split(':'));
}

// --- Main App Component ---
export default function App() {
    const [user, setUser] = useState(null);
    const [userType, setUserType] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [isChef, setIsChef] = useState(false);
    const [isRoleForced, setIsRoleForced] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthorizedDomain, setIsAuthorizedDomain] = useState(true);
    const [mealConfig, setMealConfig] = useState({ mealsToSkipByDate: {} });
    const [currentApp, setCurrentApp] = useState('meal-planner'); // New state for app switching
    const [sectionConfig, setSectionConfig] = useState({}); // Section access configuration

    // Navigation protection state
    const [showNavigationModal, setShowNavigationModal] = useState(false);
    const [pendingNavigation, setPendingNavigation] = useState(null);

    // Admin impersonation state
    const [impersonateEmail, setImpersonateEmail] = useState('');
    const [showImpersonationPanel, setShowImpersonationPanel] = useState(false);
    const [impersonatedUserData, setImpersonatedUserData] = useState(null);

useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                const checkUserRoles = httpsCallable(functions, 'checkUserRoles');
                const { data } = await checkUserRoles();
                setIsChef(data.isChef);
                setIsAdmin(data.isAdmin);

                if ((currentUser.email && currentUser.email.endsWith('@svots.edu')) || data.isChef) {
                    const forcedRoleRef = doc(db, "forcedRoles", currentUser.email);
                    const forcedRoleSnap = await getDoc(forcedRoleRef);

                    if (data.isAdmin) {
                        // If user is an admin, always let them choose their role.
                        setUserType(null);
                        setIsRoleForced(false);
                    } else if (forcedRoleSnap.exists()) {
                        // If not an admin, but has a forced role, use that.
                        setUserType(forcedRoleSnap.data().userType);
                        setIsRoleForced(true);
                    } else if (data.isChef) {
                        // If not admin or forced, but is a chef, use that.
                        setUserType('chef');
                        setIsRoleForced(true);
                    } else {
                        // Otherwise, they are a regular user without a role yet and we reject them
                        setIsAuthorizedDomain(false);
                        setUserType(null);
                    }

                    setUser(currentUser);
                    setIsAuthorizedDomain(true);
                } else {
                    setIsAuthorizedDomain(false);
                    setUserType(null);
                }
            } else {
                setUser(null);
                setUserType(null);
                setIsAdmin(false);
                setIsChef(false);
            }
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // useEffect to fetch the meal configuration
    useEffect(() => {
        if (user) {
            const configRef = doc(db, "configuration", "mealSettings");
            const unsubscribe = onSnapshot(configRef, (docSnap) => {
                setMealConfig(docSnap.exists() ? docSnap.data() : { mealsToSkipByDate: {} });
            });
            return () => unsubscribe();
        }
    }, [user]);

    // useEffect to fetch the section access configuration
    useEffect(() => {
        const loadSectionConfig = async () => {
            try {
                const configDoc = await getDoc(doc(db, 'configuration', 'sectionAccess'));
                if (configDoc.exists()) {
                    setSectionConfig(configDoc.data());
                } else {
                    // Default configuration - all sections available to all user types
                    const defaultConfig = {
                        'meal-planner': {
                            'board_paying_student': true,
                            'non_board_paying': true,
                            'staff_or_faculty': true
                        },
                        'community-service': {
                            'board_paying_student': true,
                            'non_board_paying': true,
                            'staff_or_faculty': true
                        },
                        'chapel-attendance': {
                            'board_paying_student': true,
                            'non_board_paying': true,
                            'staff_or_faculty': true
                        }
                    };
                    setSectionConfig(defaultConfig);
                }
            } catch (error) {
                console.error('Error loading section configuration:', error);
                // Set default config on error
                setSectionConfig({
                    'meal-planner': {
                        'board_paying_student': true,
                        'non_board_paying': true,
                        'staff_or_faculty': true
                    },
                    'community-service': {
                        'board_paying_student': true,
                        'non_board_paying': true,
                        'staff_or_faculty': true
                    },
                    'chapel-attendance': {
                        'board_paying_student': true,
                        'non_board_paying': true,
                        'staff_or_faculty': true
                    }
                });
            }
        };
        loadSectionConfig();
    }, []);

    // Helper function to check if a section is accessible for a user type
    const isSectionAccessible = useCallback((sectionId, currentUserType) => {
        // Admins can always access all sections
        if (isAdmin) return true;

        // Check configuration for all sections
        return sectionConfig[sectionId]?.[currentUserType] !== false;
    }, [sectionConfig, isAdmin]);

    // Protected navigation function that checks for unsaved changes
    const handleNavigation = useCallback((targetApp) => {
        if (targetApp === currentApp) {
            return; // Already on this tab
        }

        // Check if there are dirty components by dispatching a check event
        let hasDirtyComponents = false;
        const checkEvent = new CustomEvent('checkDirtyComponents', {
            detail: {
                callback: (isDirty) => { hasDirtyComponents = isDirty; }
            }
        });
        window.dispatchEvent(checkEvent);

        // If no unsaved changes, navigate directly
        if (!hasDirtyComponents) {
            setCurrentApp(targetApp);
            return;
        }

        // Show confirmation modal for unsaved changes
        setPendingNavigation(targetApp);
        setShowNavigationModal(true);
    }, [currentApp]);

    // Handle navigation modal actions
    const handleConfirmNavigation = () => {
        // Clear all dirty state when discarding changes
        window.dispatchEvent(new CustomEvent('clearAllChanges'));

        if (pendingNavigation) {
            setCurrentApp(pendingNavigation);
        }
        setShowNavigationModal(false);
        setPendingNavigation(null);
    };

    const handleCancelNavigation = () => {
        setShowNavigationModal(false);
        setPendingNavigation(null);
    };

    const handleSaveAndNavigate = () => {
        // Trigger save for all dirty components
        window.dispatchEvent(new CustomEvent('saveAllChanges'));

        // Navigate after a short delay to allow saves to complete
        setTimeout(() => {
            if (pendingNavigation) {
                setCurrentApp(pendingNavigation);
            }
            setShowNavigationModal(false);
            setPendingNavigation(null);
        }, 500);
    };

    const handleUserTypeSelect = async (type) => {
        if (user) {
            const userDocRef = doc(db, `users/${user.email}/profile/userProfile`);
            try {
            await setDoc(userDocRef, { userType: type, email: user.email, name: user.displayName });
                localStorage.setItem('meal_planner_last_user_type', type);
            setUserType(type);
            } catch (error) {
                console.error("Error setting user type: ", error);
            }
        }
    };

    const handleSwitchRole = () => setUserType(null);

    const handleSignOut = async () => {
        try {
            await signOut(auth);
            setUser(null);
            setUserType(null);
        } catch (e) {
            console.error("Error signing out:", e);
        }
    };

    // Admin impersonation functions
    const handleImpersonate = async (email) => {
        if (!isAdmin) return;

        if (!email || email === user?.email) {
            // Clear impersonation - use admin's own data
            setImpersonateEmail('');
            setImpersonatedUserData(null);
            return;
        }

        try {
            // Fetch impersonated user's data
            const forcedRoleRef = doc(db, "forcedRoles", email);
            const userProfileRef = doc(db, `users/${email}/profile/userProfile`);

            const [forcedRoleSnap, userProfileSnap] = await Promise.all([
                getDoc(forcedRoleRef),
                getDoc(userProfileRef)
            ]);

            let impersonatedType = null;
            if (forcedRoleSnap.exists()) {
                impersonatedType = forcedRoleSnap.data().userType;
            } else if (userProfileSnap.exists()) {
                impersonatedType = userProfileSnap.data().userType;
            }

            if (impersonatedType) {
                setImpersonateEmail(email);
                setImpersonatedUserData({
                    email: email,
                    userType: impersonatedType,
                    name: userProfileSnap.exists() ? userProfileSnap.data().name : email
                });
                setUserType(impersonatedType);
            } else {
                alert(`No user data found for ${email}`);
            }
        } catch (error) {
            console.error("Error impersonating user:", error);
            alert(`Error loading user data for ${email}`);
        }
    };

    const stopImpersonation = () => {
        setImpersonateEmail('');
        setImpersonatedUserData(null);
        // Reset to admin's own user type
        const adminUserType = localStorage.getItem('meal_planner_last_user_type') || 'admin';
        setUserType(adminUserType);
    };

    if (isLoading) return <div className="flex items-center justify-center h-screen"><div className="text-xl">Loading...</div></div>;

return (
        <div className="min-h-screen bg-gray-100 font-sans">
            <Header
                user={user}
                onSignOut={handleSignOut}
                onSwitchRole={handleSwitchRole}
                canSwitchRole={!isRoleForced || isAdmin}
                currentApp={currentApp}
                handleNavigation={handleNavigation}
                showAppSwitcher={user && userType && (userType === 'admin' || userType === 'board_paying_student' || userType === 'non_board_paying')}
                isAdmin={userType === 'admin'}
                userType={userType}
                isSectionAccessible={isSectionAccessible}
            />

            {/* Impersonation Banner */}
            {isAdmin && impersonatedUserData && (
                <div className="bg-yellow-100 border-b border-yellow-300 px-4 py-2 text-center">
                    <div className="text-yellow-800 font-semibold">
                        🎭 Admin Mode: Viewing as {impersonatedUserData.name} ({impersonatedUserData.email})
                    </div>
                    <div className="text-yellow-700 text-sm">
                        User Type: {impersonatedUserData.userType} • You cannot save changes in this mode
                    </div>
                </div>
            )}

            <main className="p-4">
                {!user ? <LoginScreen />
                : !isAuthorizedDomain ? <UnauthorizedDomainScreen onSignOut={handleSignOut} />
                : !userType ? <UserTypeSelection onSelect={handleUserTypeSelect} isAdmin={isAdmin} isChef={isChef} />
                : userType === 'admin' ? (
                    currentApp === 'meal-planner' ? (
                        <AdminPage mealConfig={mealConfig} />
                    ) : currentApp === 'chapel-attendance' ? (
                        <ChapelAdminDashboard db={db} functions={functions} />
                    ) : (
                        <CommunityServiceDashboard user={user} userType={userType} db={db} isAdmin={isAdmin} />
                    )
                ) : userType === 'chef' ? <ChefDashboard />
                : (userType === 'board_paying_student' || userType === 'non_board_paying') ? (
                    currentApp === 'meal-planner' && isSectionAccessible('meal-planner', userType) ? (
                        <MealSelection
                            user={user}
                            userType={userType}
                            mealConfig={mealConfig}
                            impersonateEmail={impersonateEmail}
                        />
                    ) : currentApp === 'community-service' && isSectionAccessible('community-service', userType) ? (
                        <CommunityServiceDashboard user={user} userType={userType} db={db} isAdmin={isAdmin} />
                    ) : currentApp === 'chapel-attendance' && isSectionAccessible('chapel-attendance', userType) ? (
                        <ChapelAttendanceSelection
                            user={user}
                            userType={userType}
                            db={db}
                            functions={functions}
                            impersonateEmail={impersonateEmail}
                        />
                    ) : (
                        <SectionNotAvailable currentApp={currentApp} userType={userType} isSectionAccessible={isSectionAccessible} setCurrentApp={setCurrentApp} />
                    )
                ) : (
                    currentApp === 'meal-planner' && isSectionAccessible('meal-planner', userType) ? (
                        <MealSelection
                            user={user}
                            userType={userType}
                            mealConfig={mealConfig}
                            impersonateEmail={impersonateEmail}
                        />
                    ) : currentApp === 'community-service' && isSectionAccessible('community-service', userType) ? (
                        <CommunityServiceDashboard user={user} userType={userType} db={db} isAdmin={isAdmin} />
                    ) : currentApp === 'chapel-attendance' && isSectionAccessible('chapel-attendance', userType) ? (
                        <ChapelAttendanceSelection
                            user={user}
                            userType={userType}
                            db={db}
                            functions={functions}
                            impersonateEmail={impersonateEmail}
                        />
                    ) : (
                        <SectionNotAvailable currentApp={currentApp} userType={userType} isSectionAccessible={isSectionAccessible} setCurrentApp={setCurrentApp} />
                    )
                )}
            </main>

            {/* Navigation Confirmation Modal */}
            {showNavigationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md mx-4 shadow-xl">
                        <div className="text-center">
                            <div className="text-4xl mb-4">⚠️</div>
                            <h3 className="text-lg font-bold text-gray-900 mb-2">
                                You have unsaved changes
                            </h3>
                            <p className="text-gray-600 mb-6">
                                You have unsaved changes that will be lost if you navigate away. What would you like to do?
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={handleSaveAndNavigate}
                                    className="bg-[#782953] text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-colors font-medium"
                                >
                                    Save & Continue
                                </button>
                                <button
                                    onClick={handleConfirmNavigation}
                                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors font-medium"
                                >
                                    Discard Changes
                                </button>
                                <button
                                    onClick={handleCancelNavigation}
                                    className="bg-gray-100 text-gray-600 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Global Save Reminder - Fixed Position */}
            <GlobalSaveReminder />

            {/* Admin Impersonation Panel */}
            {isAdmin && (
                <div className="fixed bottom-4 right-4 z-50">
                    {showImpersonationPanel ? (
                        <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-4 w-80">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-bold text-[#782953]">Admin: View as User</h3>
                                <button
                                    onClick={() => setShowImpersonationPanel(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>

                            {impersonatedUserData && (
                                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
                                    <div className="font-semibold text-yellow-800">Currently viewing as:</div>
                                    <div className="text-yellow-700">{impersonatedUserData.name}</div>
                                    <div className="text-yellow-600">{impersonatedUserData.email}</div>
                                    <div className="text-yellow-600">Type: {impersonatedUserData.userType}</div>
                                </div>
                            )}

                            <div className="space-y-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        User Email:
                                    </label>
                                    <input
                                        type="email"
                                        placeholder="afarha@svots.edu"
                                        value={impersonateEmail}
                                        onChange={(e) => setImpersonateEmail(e.target.value)}
                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                                    />
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleImpersonate(impersonateEmail)}
                                        disabled={!impersonateEmail.trim()}
                                        className="flex-1 bg-[#782953] text-white px-3 py-2 rounded text-sm hover:bg-opacity-90 disabled:bg-gray-300"
                                    >
                                        View as User
                                    </button>
                                    <button
                                        onClick={stopImpersonation}
                                        className="flex-1 bg-gray-600 text-white px-3 py-2 rounded text-sm hover:bg-gray-700"
                                    >
                                        View as Self
                                    </button>
                                </div>
                            </div>

                            <div className="mt-3 text-xs text-gray-500">
                                ⚠️ You cannot save changes while impersonating another user.
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={() => setShowImpersonationPanel(true)}
                            className="bg-purple-600 hover:bg-purple-700 text-white p-3 rounded-full shadow-lg transition-colors"
                            title="Admin: View as another user"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

// --- Components ---

function UnauthorizedDomainScreen({ onSignOut }) {
    return (
        <div className="max-w-lg mx-auto mt-16 text-center bg-white p-8 sm:p-10 rounded-2xl shadow-xl border border-gray-200">
            <img src="/svots_logo.png" alt="SVOTS Logo" className="mx-auto mb-6 h-20" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/200x80/cccccc/000000?text=SVOTS+Logo'; }} />
            <h2 className="text-2xl sm:text-3xl font-bold text-red-800 mb-4">Access Denied</h2>
            <p className="text-gray-600 mb-8 text-base sm:text-lg">
                This meal planner is restricted to certain users, generally with an <strong className="font-semibold text-gray-800">@svots.edu</strong> email account.
            </p>
            <button onClick={onSignOut} className="w-full text-lg py-3 px-4 bg-red-700 text-white font-semibold rounded-lg shadow-md hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-75 transition-all duration-300 ease-in-out transform hover:-translate-y-1">
                Sign Out
            </button>
        </div>
    );
}

function LoginScreen() {
    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(auth, provider);
        } catch (error) {
            console.error("Error during Google sign-in:", error);
        }
    };

    return (
        <div className="max-w-md mx-auto mt-16 text-center bg-white p-8 sm:p-12 rounded-2xl shadow-xl border border-gray-200">
            <img src="/svots_logo.png" alt="SVOTS Logo" className="mx-auto mb-8 h-24" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/200x80/cccccc/000000?text=SVOTS+Logo'; }} />
            <h2 className="text-3xl sm:text-4xl font-bold text-[#782953] mb-4">Refectory Meal Planner</h2>
            <p className="text-gray-600 mb-10 text-lg">Please sign in to continue.</p>
            <button onClick={handleGoogleSignIn} className="w-full flex items-center justify-center gap-3 text-lg py-3 px-4 bg-white border-2 border-gray-300 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#782953] focus:ring-opacity-75 transition-all duration-300 ease-in-out transform hover:-translate-y-1">
                <svg className="w-6 h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"></path><path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"></path><path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"></path><path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.012 36.49 44 30.686 44 24c0-1.341-.138-2.65-.389-3.917z"></path></svg>
                Sign In with Google
            </button>
        </div>
    );
}

function Header({ user, onSignOut, onSwitchRole, canSwitchRole, currentApp, handleNavigation, showAppSwitcher, isAdmin, userType, isSectionAccessible }) {
    return (
        <header className="bg-white shadow-md sticky top-0 z-20 border-b-4 border-yellow-400">
            <div className="container mx-auto px-4 sm:px-6 py-3">
                {/* Top row with logo and user info */}
                <div className="flex justify-between items-center">
                    <img src="/svots_logo.png" alt="SVOTS Logo" className="h-14" onError={(e) => { e.target.onerror = null; e.target.src='https://placehold.co/150x50/cccccc/000000?text=Logo'; }} />
                    {user && (
                        <div className="flex items-center gap-2 sm:gap-4">
                            <div className="text-right hidden sm:block">
                                <p className="font-semibold text-gray-800">{user.displayName}</p>
                                <p className="text-xs text-gray-500">{user.email}</p>
                            </div>
                            {canSwitchRole && <button onClick={onSwitchRole} className="bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-all">Switch Role</button>}
                            <button onClick={onSignOut} className="bg-[#782953] text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#782953] transition-all">Sign Out</button>
                        </div>
                    )}
                </div>

                {/* Tab-style app switcher */}
                {showAppSwitcher && (
                    <div className="mt-4 border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {isSectionAccessible('meal-planner', userType) && (
                                <button
                                    onClick={() => handleNavigation('meal-planner')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        currentApp === 'meal-planner'
                                            ? 'border-[#782953] text-[#782953]'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Meal Planner
                                </button>
                            )}
                            {isSectionAccessible('community-service', userType) && (
                                <button
                                    onClick={() => handleNavigation('community-service')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        currentApp === 'community-service'
                                            ? 'border-[#782953] text-[#782953]'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Community Service
                                </button>
                            )}
                            {/* Chapel Attendance tab - admin configurable */}
                            {isSectionAccessible('chapel-attendance', userType) && (
                                <button
                                    onClick={() => handleNavigation('chapel-attendance')}
                                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                                        currentApp === 'chapel-attendance'
                                            ? 'border-[#782953] text-[#782953]'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    Chapel Attendance
                                </button>
                            )}
                        </nav>
                    </div>
                )}
            </div>
        </header>
    );
}
function UserTypeSelection({ onSelect, isAdmin }) {
    const [lastUserType, setLastUserType] = useState(null);

    useEffect(() => {
        setLastUserType(localStorage.getItem('meal_planner_last_user_type'));
    }, []);

    let userTypes = [
        { id: 'board_paying_student', name: 'Board Paying Student' },
        { id: 'non_board_paying', name: 'Non-Board Paying Student' },
        { id: 'staff_or_faculty', name: 'Staff / Faculty' },
    ];

    if (isAdmin) {
        userTypes.push({ id: 'chef', name: 'Chef Dashboard' });
        userTypes.push({ id: 'admin', name: 'Admin Page' });
    }

    return (
        <div className="max-w-md mx-auto mt-10 bg-white p-8 rounded-2xl shadow-xl border border-gray-200">
            <h2 className="text-3xl font-bold text-center text-[#782953] mb-8">Select Your Role</h2>
            <div className="space-y-4">
                {userTypes.map(type => {
                    const isLastSelected = type.id === lastUserType;
                    const buttonClass = isLastSelected
                        ? "w-full text-xl py-4 px-4 bg-yellow-400 text-[#782953] font-bold rounded-xl shadow-xl ring-4 ring-yellow-200 transition-all duration-300 ease-in-out transform scale-105"
                        : "w-full text-lg py-3 px-4 bg-[#782953] text-white font-semibold rounded-lg shadow-md hover:bg-opacity-90 focus:outline-none focus:ring-2 focus:ring-[#9a4e7a] transition-all duration-300 ease-in-out transform hover:-translate-y-1 hover:shadow-xl";

                    return <button key={type.id} onClick={() => onSelect(type.id)} className={buttonClass}>{type.name}</button>;
                })}
            </div>
        </div>
    );
}

function MealSelection({ user, userType, mealConfig, impersonateEmail }) {
     const [currentDate, setCurrentDate] = useState(new Date());
    const [selections, setSelections] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const [showLegend, setShowLegend] = useState(false);
    const [showPast, setShowPast] = useState(false);
    const monthYearFormat = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

    // Track pending updates to prevent race conditions
    const pendingUpdatesRef = useRef(new Set());

    const saveSelections = useCallback(async () => {
        // Prevent saving when impersonating another user
        if (impersonateEmail) {
            alert("Cannot save changes while impersonating another user. Please stop impersonation to save changes.");
            return;
        }

        // Group selections by month to save to correct documents
        const selectionsByMonth = {};

        for (const [dateString, daySelections] of Object.entries(selections)) {
            const date = new Date(dateString + 'T00:00:00');
            const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

            if (!selectionsByMonth[monthId]) {
                selectionsByMonth[monthId] = {};
            }
            selectionsByMonth[monthId][dateString] = daySelections;
        }

        try {
            // Save each month's selections to its respective document
            const savePromises = Object.entries(selectionsByMonth).map(([monthId, monthSelections]) => {
                const docRef = doc(db, `users/${user.email}/selections/${monthId}`);
                return setDoc(docRef, monthSelections, { merge: true });
            });

            await Promise.all(savePromises);
            setIsDirty(false);
        } catch (e) {
            console.error('Error saving selections:', e);
        }
    }, [selections, impersonateEmail, user.email]);

    // Notify global save reminder when isDirty changes
    useEffect(() => {
        window.dispatchEvent(new CustomEvent('saveReminderUpdate', {
            detail: {
                componentId: 'meal-selection',
                isDirty,
                saveFunction: isDirty ? saveSelections : null
            }
        }));
    }, [isDirty, saveSelections]);

    const getBookingWindow = useCallback(() => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const lastFriday = new Date(today);
        lastFriday.setDate(today.getDate() - (today.getDay() + 2) % 7);
        const endDate = new Date(lastFriday);
        endDate.setDate(endDate.getDate() + 180); // 4 months ahead
        return { start: today, end: endDate };
    }, []);

    const [bookingWindow, setBookingWindow] = useState(getBookingWindow());

    const generateCalendarDays = useCallback(() => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = [];

        // Populate days from previous month to fill the first week starting on Sunday
        if (firstDayOfMonth > 0) {
            const prevMonthLastDate = new Date(year, month, 0).getDate();
            const prevMonth = month === 0 ? 11 : month - 1;
            const prevMonthYear = month === 0 ? year - 1 : year;
            for (let i = prevMonthLastDate - firstDayOfMonth + 1; i <= prevMonthLastDate; i++) {
                days.push(new Date(prevMonthYear, prevMonth, i));
            }
        }

        // Populate days for the current month
        for (let i = 1; i <= daysInMonth; i++) {
            days.push(new Date(year, month, i));
        }
        return days;
    }, [currentDate]);

    useEffect(() => {
        const timer = setInterval(() => setBookingWindow(getBookingWindow()), 60000);
        return () => clearInterval(timer);
    }, [getBookingWindow]);

    useEffect(() => {
        if (!user) return;

        // Determine which user's data to load (impersonated user or current user)
        const emailToLoad = impersonateEmail || user.email;

        // Get all the months that might be displayed in this calendar view
        const calendarDays = generateCalendarDays();
        const monthsToLoad = new Set();

        calendarDays.forEach(date => {
            const monthId = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            monthsToLoad.add(monthId);
        });

        // Track selections by month to properly merge them
        const selectionsTracker = {};
        const unsubscribes = [];

        const updateSelections = () => {
            // Merge all month selections into one object
            const mergedSelections = {};
            Object.values(selectionsTracker).forEach(monthData => {
                Object.assign(mergedSelections, monthData);
            });
            setSelections(mergedSelections);
            setIsDirty(false);
        };

        // Load selections from all relevant months
        monthsToLoad.forEach(monthId => {
            const docRef = doc(db, `users/${emailToLoad}/selections/${monthId}`);
            const unsubscribe = onSnapshot(docRef, (docSnap) => {
                if (docSnap.exists()) {
                    selectionsTracker[monthId] = docSnap.data();
                } else {
                    selectionsTracker[monthId] = {};
                }
                updateSelections();
            });
            unsubscribes.push(unsubscribe);
        });

        return () => unsubscribes.forEach(unsub => unsub());
    }, [user, currentDate, generateCalendarDays, impersonateEmail]);

    const handleCycleState = (date, meal) => {
        // Prevent changes when impersonating another user
        // if (impersonateEmail) {
        //     alert("Cannot modify selections while impersonating another user.");
        //     return;
        // }

        // Use timezone-safe date formatting to prevent date shifts
        const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        const updateKey = `${dateString}-${meal}`;

        // Prevent duplicate rapid clicks on the same meal
        if (pendingUpdatesRef.current.has(updateKey)) {
            return;
        }

        // Track this update
        pendingUpdatesRef.current.add(updateKey);

        setIsDirty(true);

        // Use functional update that doesn't rely on previous state from closure
        setSelections(currentSelections => {
            // Clone the entire selections object
            const newSelections = JSON.parse(JSON.stringify(currentSelections));

            // Ensure the date exists
            if (!newSelections[dateString]) {
                newSelections[dateString] = {};
            }

            // Get current meal state
            const currentMealSelection = newSelections[dateString][meal] || { selected: false, packed: false };

            const canBePacked = userType === 'board_paying_student';

            // Create new meal selection
            let newMealSelection;
            if (!currentMealSelection.selected && !currentMealSelection.packed) {
                newMealSelection = { selected: true, packed: false };
            } else if (currentMealSelection.selected && !currentMealSelection.packed && canBePacked) {
                newMealSelection = { selected: true, packed: true };
            } else {
                newMealSelection = { selected: false, packed: false };
            }

            // Update the meal selection
            newSelections[dateString][meal] = newMealSelection;

            return newSelections;
        });

        // Clear the pending update after a brief delay
        setTimeout(() => {
            pendingUpdatesRef.current.delete(updateKey);
        }, 100);
    };

    const calendarDays = generateCalendarDays();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const getNewYorkDate = (date = new Date()) => {
        return new Date(date.toLocaleString("en-US", { timeZone: "America/New_York" }));
    };

    const getNewYorkMidnight = (date = new Date()) => {
        const nyDate = getNewYorkDate(date);
        nyDate.setHours(0, 0, 0, 0); // Set to midnight in NY timezone
        return nyDate;
    };

    const getCutoffTime = () => {
        const nyMidnight = getNewYorkMidnight();
        const cutoffTime = new Date(nyMidnight);
        cutoffTime.setHours(CUTOFF_HOUR + CUTOFF_BUFFER_HOURS);
        return cutoffTime;
    };

    const isMealLocked = (meal, date) => {
        const currentTimeInNY = getNewYorkDate(date);
        const cutoffTime = getCutoffTime();
        return currentTimeInNY <= cutoffTime;
    };

    const availableMealsForDay = {
        board_paying_student: {
        /* Sun */ 0: ['Breakfast', 'Lunch', 'Dinner'], // no breakfast really, but hardcoded away later to show placeholder
        /* Mon */ 1: ['Breakfast', 'Lunch', 'Dinner'],
        /* Tue */ 2: ['Breakfast', 'Lunch', 'Dinner'],
        /* Wed */ 3: ['Breakfast', 'Lunch', 'Dinner'],
        /* Thu */ 4: ['Breakfast', 'Lunch', 'Dinner'],
        /* Fri */ 5: ['Breakfast', 'Lunch', 'Dinner'],
        /* Sat */ 6: ['Breakfast', 'Lunch', 'Dinner']
        },
        non_board_paying: {
        /* Sun */ 0: [],
        /* Mon */ 1: ['Lunch'],
        /* Tue */ 2: ['Lunch'],
        /* Wed */ 3: ['Lunch'],
        /* Thu */ 4: ['Lunch'],
        /* Fri */ 5: [],
        /* Sat */ 6: []
        },
        staff_or_faculty: {
        /* Sun */ 0: [],
        /* Mon */ 1: ['Lunch'],
        /* Tue */ 2: ['Lunch'],
        /* Wed */ 3: ['Lunch'],
        /* Thu */ 4: ['Lunch'],
        /* Fri */ 5: [],
        /* Sat */ 6: []
        }
    }[userType] || {};

    return (
        <div className="max-w-4xl mx-auto bg-white p-2 sm:p-4 rounded-2xl shadow-xl border relative pb-24">
            {showLegend && <LegendModal onClose={() => setShowLegend(false)} />}
            <div className="flex justify-between items-center mb-4">
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() - 1)))} className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300">&lt;</button>
                <h2 className="text-xl sm:text-3xl font-bold text-[#782953]">{monthYearFormat.format(currentDate)}</h2>
                <button onClick={() => setCurrentDate(new Date(currentDate.setMonth(currentDate.getMonth() + 1)))} className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300">&gt;</button>
            </div>

            <div className="grid grid-cols-7 gap-1">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className="text-center font-bold text-gray-600 mb-1 text-xs sm:text-sm">{day}</div>)}
                    {
                        !showPast && (() => {
                            if (currentDate.getFullYear() === today.getFullYear() && currentDate.getMonth() === today.getMonth()) {
                                const todayIndex = today.getDay();
                                return Array.from({ length: todayIndex }).map((_, index) => (
                                    <div key={`empty-${index}`} className="border rounded-md bg-gray-50 min-h-[90px]"></div>
                                ));
                            } else {
                                return null;
                            }
                        })()
                    }

                {calendarDays.map((date, index) => {
                    // poor man's hardcoded rule.  For tperkins@svots.edu, only Tuesdays are allowed.
                    const forceNotBookable = (user.email === 'tperkins@svots.edu' && date.getDay() !== 2);
                    // Use timezone-safe date formatting to prevent date shifts
                    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                    const daySelections = selections[dateString] || {};
                    const isPast = date < today;
                    const isBookable = !forceNotBookable && date <= bookingWindow.end;
                    if (isPast && !showPast) return null;

                    // New: check if all meals for this day are locked
                    const mealsForDay = availableMealsForDay[date.getDay()] || [];
                    const allMealsLocked = mealsForDay.length > 0 && mealsForDay.every(meal => isMealLocked(meal, date));

                    const grayOut = !isBookable || allMealsLocked;

                    return (
                        <div
                            key={dateString}
                            className={`border rounded-md p-1 ${grayOut ? 'bg-gray-50 opacity-70' : 'bg-white'}`}
                        >
                            <div className="font-bold text-center text-xs mb-1">{date.getDate()}</div>
                            <div className="space-y-1">
                                {mealsForDay.map(meal => {
                                    if ((date.getDay() === 0 && meal === 'Breakfast') ||
                                        (mealConfig.mealsToSkipByDate[dateString] && mealConfig.mealsToSkipByDate[dateString].includes(meal))) {
                                        return (
                                            <div key={meal} className="w-full h-8 border rounded-md flex items-center justify-center bg-gray-100 text-gray-400 text-xs italic">
                                                {meal.charAt(0)} (No Service)
                                            </div>
                                        );
                                    }
                                    const mealSelection = daySelections[meal] || {};
                                    const locked = isMealLocked(meal, date);

                                    let stateClass = "bg-gray-200";
                                    if (mealSelection.selected && mealSelection.packed) stateClass = "bg-yellow-400";
                                    else if (mealSelection.selected) stateClass = "bg-green-400";

                                    return (
                                        <button
                                            key={meal}
                                            disabled={isPast || locked}
                                            onClick={() => handleCycleState(date, meal)}
                                            className={`w-full h-8 border rounded-md flex items-center justify-center transition-all duration-200 relative ${stateClass} disabled:cursor-not-allowed disabled:opacity-70`}
                                        >
                                            <span className="text-xs font-semibold text-gray-600 mr-2">{meal.charAt(0)}</span>
                                            {mealSelection.selected && mealSelection.packed && <span className="text-xl font-bold text-white">🥡</span>}
                                            {mealSelection.selected && !mealSelection.packed && <span className="text-2xl text-white">✓</span>}
                                            {!mealSelection.selected && !mealSelection.packed && <span className="text-2xl text-gray-400">×</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-center items-center gap-4 mt-4">
                <button onClick={() => setShowLegend(true)} className="text-sm text-[#782953] hover:underline">Show Legend</button>
                <button onClick={() => setShowPast(!showPast)} className="text-sm text-[#782953] hover:underline">{showPast ? 'Hide' : 'Show'} Past Days</button>
            </div>
        </div>
    );
}

function LegendModal({ onClose }) {
    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4" onClick={onClose}>
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm w-full" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold mb-4 text-[#782953]">How to Use the Calendar</h3>
                <p className="text-gray-600 mb-4">Tap any day's meal button to cycle through the options.</p>
                <div className="space-y-3">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-2xl text-gray-400">×</div>
                        <span><strong className="font-semibold">Not Attending:</strong> You will not be served this meal.</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-400 rounded flex items-center justify-center text-2xl text-white">✓</div>
                        <span><strong className="font-semibold">Attending:</strong> You will be served this meal in the refectory.</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-yellow-400 rounded flex items-center justify-center text-xl font-bold text-white">🥡</div>
                        <span><strong className="font-semibold">Packed Meal:</strong> Your meal will be packed for takeout. (Board students only)</span>
                    </div>
                </div>
                <button onClick={onClose} className="mt-6 w-full bg-gray-200 text-gray-700 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300">Close</button>
            </div>
        </div>
    );
}

function ChefDashboard() {
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [weeklyData, setWeeklyData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [modalData, setModalData] = useState(null);

    const getWeekRange = (date) => {
        const start = new Date(date);
        start.setDate(start.getDate() - start.getDay());
        start.setHours(0, 0, 0, 0);
        const end = new Date(start);
        end.setDate(end.getDate() + 6);
        end.setHours(23, 59, 59, 999);
        return { start, end };
    };

    const calculateStats = useCallback((data) => {
        const dailyBreakdown = {};
        for (const dateString in data) {
            if(!dailyBreakdown[dateString]) dailyBreakdown[dateString] = {};
            for (const meal in data[dateString]) {
                const { selected, packed } = data[dateString][meal];
                if(!dailyBreakdown[dateString][meal]) dailyBreakdown[dateString][meal] = { selected: 0, packed: 0 };
                dailyBreakdown[dateString][meal].selected += selected;
                dailyBreakdown[dateString][meal].packed += packed;
            }
        }
        return { dailyBreakdown };
    }, []);

    const fetchDataForWeek = useCallback(async (date) => {
        try {
            const getWeeklyStats = httpsCallable(functions, 'getWeeklyStats');
            const response = await getWeeklyStats({ date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` });
            return calculateStats(response.data);
        } catch (error) {
            console.error("Error fetching weekly stats:", error);
            return calculateStats({});
        }
    }, [calculateStats]);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const currentWeekStats = await fetchDataForWeek(selectedDate);
        setWeeklyData(currentWeekStats);
        setIsLoading(false);
    }, [selectedDate, fetchDataForWeek]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleCellClick = async (date, meal) => {
        setModalData({ date, meal, attendees: [], isLoading: true });
        try {
            const getMealAttendees = httpsCallable(functions, 'getMealAttendees');
            const response = await getMealAttendees({ date: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`, meal });
            setModalData({ date, meal, attendees: response.data, isLoading: false });
        } catch (error) {
            console.error("Error fetching attendees:", error);
            setModalData({ date, meal, attendees: [], isLoading: false, error: "Could not load attendees." });
        }
    };

    const weekRange = getWeekRange(selectedDate);
    const weekFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

    return (
        <div className="max-w-7xl mx-auto bg-white p-6 sm:p-8 rounded-2xl shadow-xl border">
            {modalData && <MealAttendeesModal data={modalData} onClose={() => setModalData(null)} />}
            <div className="flex justify-between items-center mb-6">
                <button onClick={() => setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() - 7)))} className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300">&lt; Prev Week</button>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#782953] text-center">Chef Dashboard: <br className="sm:hidden"/>{weekFormat.format(weekRange.start)} - {weekFormat.format(weekRange.end)}</h2>
                <button onClick={() => setSelectedDate(new Date(selectedDate.setDate(selectedDate.getDate() + 7)))} className="bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-lg hover:bg-gray-300">Next Week &gt;</button>
            </div>

            <div className="flex justify-end items-center gap-4 mb-6">
                <button onClick={loadData} disabled={isLoading} className="bg-[#782953] text-white font-bold py-2 px-4 rounded-lg hover:bg-opacity-90 disabled:bg-gray-400">
                    {isLoading ? 'Refreshing...' : 'Force Refresh'}
                </button>
            </div>

            {isLoading ? <div className="text-center p-10">Loading statistics...</div> : weeklyData ?
                <>
                    <h3 className="text-xl font-bold text-[#782953] mb-4">Meals to Prepare</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full border-collapse">
                             <thead>
                                <tr className="bg-[#782953] text-white">
                                    <th className="p-3 font-semibold text-left uppercase tracking-wider rounded-tl-lg">Day</th>
                                    <th className="p-3 font-semibold text-center uppercase tracking-wider">Breakfast</th>
                                    <th className="p-3 font-semibold text-center uppercase tracking-wider">Lunch</th>
                                    <th className="p-3 font-semibold text-center uppercase tracking-wider rounded-tr-lg">Dinner</th>
                                </tr>
                            </thead>
                            <tbody>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => {
                                    const date = new Date(weekRange.start);
                                    date.setDate(date.getDate() + i);
                                    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                                    const dayData = weeklyData.dailyBreakdown?.[dateString] || {};

                                    const MealCell = ({ meal, data }) => (
                                        <td className="p-3 text-center">
                                            <button onClick={() => handleCellClick(date, meal)} className="hover:underline">
                                                {data?.selected || 0} / {data?.packed || 0}{data?.packed > 0 && " 🥡"}
                                            </button>
                                        </td>
                                    );

                                    const meals = ['Breakfast', 'Lunch', 'Dinner'];

                                    return (
                                        <tr key={day} className="border-b border-gray-200 bg-white">
                                            <td className="p-3 font-bold text-gray-800">
                                                {day} <span className="text-sm text-gray-500">({date.getDate()})</span>
                                            </td>
                                            {meals.map(meal => (
                                                <MealCell key={meal} meal={meal} data={dayData[meal]} />
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            : <div className="text-center text-gray-500 p-8">No data available for this week.</div>
            }
        </div>
    );
}

// --- Admin Page and Components ---
// Replace your existing AdminPage function with all of the following:

function MealAttendeesModal({ data, onClose }) {
    const { date, meal, attendees, isLoading, error } = data;
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'ascending' });

    const sortedAttendees = useMemo(() => {
        let sortableItems = [...attendees];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [attendees, sortConfig]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4" onClick={onClose}>
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-[#782953]">{meal} Attendees for {date.toLocaleDateString()}</h3>
                    <button onClick={onClose} className="text-2xl text-gray-500 hover:text-gray-800">&times;</button>
                </div>
                {isLoading ? <p>Loading attendees...</p> : error ? <p className="text-red-500">{error}</p> :
                    <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr>
                                    <th onClick={() => requestSort('name')} className="cursor-pointer p-2 border-b-2">Name / Email</th>
                                    <th onClick={() => requestSort('packed')} className="cursor-pointer p-2 border-b-2 text-center">Packed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAttendees.map(person => (
                                    <tr key={person.email} className="hover:bg-gray-50">
                                        <td className="p-2 border-b">{person.name || person.email}</td>
                                        <td className="p-2 border-b text-center">{person.packed ? '🥡' : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                }
            </div>
        </div>
    );
}

function AdminPage({ mealConfig }) {
    return (
        <div className="container mx-auto">
            <h1 className="text-3xl font-bold mb-6 text-[#782953]">Admin Dashboard</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <ForceUserRole />
                <BulkForceMeals />
            </div>
            <div className="mt-8"><SectionAccessManager /></div>
            <div className="mt-8"><MealConfigManager initialConfig={mealConfig} /></div>
            <div className="mt-8"><ForcedRolesList /></div>
            <div className="mt-8"><SeedDataDownloader /></div>
        </div>
    );
}

function ForceUserRole() {
    const [emails, setEmails] = useState('');
    const [role, setRole] = useState('board_paying_student');
    const [status, setStatus] = useState({ message: '', isError: false });
    const [isLoading, setIsLoading] = useState(false);

    const handleForceRole = async (e) => {
        e.preventDefault();
        const emailList = emails.split(/[\n,\t]/).map(e => e.trim()).filter(Boolean);
        if (emailList.length === 0) return;
        setIsLoading(true);
        setStatus({ message: 'Processing...', isError: false });
        try {
            const forceUserRole = httpsCallable(functions, 'forceUserRole');
            const result = await forceUserRole({ emails: emailList, role });
            setStatus({ message: result.data.message, isError: false });
            setEmails('');
        } catch (error) {
            setStatus({ message: error.message, isError: true });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Assign User Role</h2>
            <form onSubmit={handleForceRole}>
                <div className="mb-4">
                    <label htmlFor="emails" className="block text-gray-700 font-medium mb-2">User Emails (comma-separated)</label>
                    <textarea id="emails" rows="3" value={emails} onChange={(e) => setEmails(e.target.value)} className="w-full p-2 border rounded-md" placeholder="user1@svots.edu, ..."></textarea>
                </div>
                <div className="mb-4">
                    <label htmlFor="role" className="block text-gray-700 font-medium mb-2">Role to Assign</label>
                    <select id="role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full p-2 border rounded-md bg-white">
                        {Object.keys(ROLES).map(roleKey => <option key={roleKey} value={roleKey}>{ROLES[roleKey]}</option>)}
                    </select>
                </div>
                <button type="submit" disabled={isLoading} className="w-full bg-[#782953] text-white font-bold py-2 px-4 rounded-md hover:bg-opacity-90 disabled:bg-gray-400">Assign Role</button>
            </form>
            {status.message && <div className={`mt-4 p-3 rounded-md text-center ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{status.message}</div>}
        </div>
    );
}

function BulkForceMeals() {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [userType, setUserType] = useState('board_paying_student');
    const [meals, setMeals] = useState({ Breakfast: false, Lunch: false, Dinner: false });
    const [status, setStatus] = useState({ message: '', isError: false });
    const [isLoading, setIsLoading] = useState(false);

    const handleBulkForce = async (e) => {
        e.preventDefault();
        if (!startDate || !endDate || !userType) return;
        setIsLoading(true);
        setStatus({ message: 'Processing...', isError: false });
        try {
            const bulkForceMealSelections = httpsCallable(functions, 'bulkForceMealSelections');
            const result = await bulkForceMealSelections({ startDateString: startDate, endDateString: endDate, userType, meals });
            setStatus({ message: result.data.message, isError: false });
        } catch (error) {
            setStatus({ message: error.message, isError: true });
        } finally {
            setIsLoading(false);
        }
    };

    // Add handler for start date change
    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        if (!endDate) {
            setEndDate(newStartDate);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Bulk Force Meal Selections</h2>
            <form onSubmit={handleBulkForce}>
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                        <label className="block font-medium mb-1">Start Date</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={handleStartDateChange}
                            className="w-full p-2 border rounded-md"
                            required
                        />
                    </div>
                    <div>
                        <label className="block font-medium mb-1">End Date</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={e => setEndDate(e.target.value)}
                            className="w-full p-2 border rounded-md"
                            required
                        />
                    </div>
                </div>
                <div className="mb-4">
                    <label className="block font-medium mb-1">Target User Role</label>
                    <select value={userType} onChange={e => setUserType(e.target.value)} className="w-full p-2 border rounded-md bg-white">
                        {Object.keys(ROLES).filter(r => r !== 'admin' && r !== 'chef').map(roleKey => <option key={roleKey} value={roleKey}>{ROLES[roleKey]}</option>)}
                    </select>
                </div>
                <div className="mb-4">
                    <label className="block font-medium mb-1">Meals to Force "Attending"</label>
                    <div className="flex gap-4">
                        {Object.keys(meals).map(meal => (
                            <label key={meal} className="flex items-center gap-1"><input type="checkbox" checked={meals[meal]} onChange={() => setMeals(prev => ({ ...prev, [meal]: !prev[meal] }))} />{meal}</label>
                        ))}
                    </div>
                </div>
                <button type="submit" disabled={isLoading} className="w-full bg-[#782953] text-white font-bold py-2 px-4 rounded-md hover:bg-opacity-90 disabled:bg-gray-400">Force Selections</button>
            </form>
            {status.message && <div className={`mt-4 p-3 rounded-md text-center ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{status.message}</div>}
        </div>
    );
}

function MealConfigManager({ initialConfig }) {
    const [config, setConfig] = useState(initialConfig.mealsToSkipByDate || {});
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [meals, setMeals] = useState({ Breakfast: false, Lunch: false, Dinner: false });
    const [status, setStatus] = useState({ message: '', isError: false });
    const [isLoading, setIsLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'ascending' });

    useEffect(() => {
        setConfig(initialConfig.mealsToSkipByDate || {});
    }, [initialConfig]);

    // Handle start date change and auto-set end date
    const handleStartDateChange = (e) => {
        const newStartDate = e.target.value;
        setStartDate(newStartDate);
        if (!endDate) {
            setEndDate(newStartDate);
        }
    };

    const handleSort = (key) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'ascending' ? 'descending' : 'ascending'
        }));
    };

    const sortedDates = useMemo(() => {
        const dates = Object.keys(config).map(dateKey => ({
            date: dateKey,
            breakfast: config[dateKey].includes('Breakfast'),
            lunch: config[dateKey].includes('Lunch'),
            dinner: config[dateKey].includes('Dinner')
        }));

        return dates.sort((a, b) => {
            if (sortConfig.key === 'date') {
                return sortConfig.direction === 'ascending'
                    ? a.date.localeCompare(b.date)
                    : b.date.localeCompare(a.date);
            }
            return sortConfig.direction === 'ascending'
                ? (a[sortConfig.key] === b[sortConfig.key] ? 0 : a[sortConfig.key] ? 1 : -1)
                : (a[sortConfig.key] === b[sortConfig.key] ? 0 : a[sortConfig.key] ? -1 : 1);
        });
    }, [config, sortConfig]);

    const handleRemoveMeal = (dateKey, meal) => {
        setConfig(prev => {
            const newConfig = { ...prev };
            newConfig[dateKey] = newConfig[dateKey].filter(m => m !== meal);
            if (newConfig[dateKey].length === 0) {
                delete newConfig[dateKey];
            }
            return newConfig;
        });
    };

    const handleAddDates = () => {
        if (!startDate) {
            setStatus({ message: 'Please select a start date.', isError: true });
            return;
        }
        const selectedMeals = Object.keys(meals).filter(m => meals[m]);
        if (selectedMeals.length === 0) {
            setStatus({ message: 'Please select at least one meal to skip.', isError: true });
            return;
        }

        const newConfig = { ...config };
        const start = new Date(startDate + 'T00:00:00');
        const end = endDate ? new Date(endDate + 'T00:00:00') : new Date(start);

        for (let d = start; d <= end; d.setDate(d.getDate() + 1)) {
            const dateKey = `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`;
            const existingMeals = new Set(newConfig[dateKey] || []);
            selectedMeals.forEach(meal => existingMeals.add(meal));
            newConfig[dateKey] = Array.from(existingMeals).sort();
        }

        setConfig(newConfig);
        setStartDate('');
        setEndDate('');
        setMeals({ Breakfast: false, Lunch: false, Dinner: false });
        setStatus({ message: '', isError: false });
    };

    const handleSaveChanges = async () => {
        setIsLoading(true);
        setStatus({ message: 'Saving...', isError: false });
        try {
            const updateMealConfiguration = httpsCallable(functions, 'updateMealConfiguration');
            await updateMealConfiguration({ mealsToSkipByDate: config });
            setStatus({ message: 'Configuration saved successfully!', isError: false });
        } catch (error) {
            setStatus({ message: error.message, isError: true });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Manage Holidays / Skipped Meals</h2>
            <div className="overflow-x-auto border rounded-md mb-4">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('date')}>
                                Date {sortConfig.key === 'date' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('breakfast')}>
                                Breakfast {sortConfig.key === 'breakfast' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('lunch')}>
                                Lunch {sortConfig.key === 'lunch' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort('dinner')}>
                                Dinner {sortConfig.key === 'dinner' && (sortConfig.direction === 'ascending' ? '↑' : '↓')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {sortedDates.map(({ date, breakfast, lunch, dinner }) => (
                            <tr key={date} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">{date}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {breakfast && (
                                        <button onClick={() => handleRemoveMeal(date, 'Breakfast')} className="text-red-500 hover:text-red-700">
                                            Remove
                                        </button>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {lunch && (
                                        <button onClick={() => handleRemoveMeal(date, 'Lunch')} className="text-red-500 hover:text-red-700">
                                            Remove
                                        </button>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {dinner && (
                                        <button onClick={() => handleRemoveMeal(date, 'Dinner')} className="text-red-500 hover:text-red-700">
                                            Remove
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="border-t pt-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                    <div>
                        <label className="block font-medium mb-1">Start Date</label>
                        <input type="date" value={startDate} onChange={handleStartDateChange} className="w-full p-2 border rounded-md"/>
                    </div>
                    <div>
                        <label className="block font-medium mb-1">End Date (Optional)</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border rounded-md"/>
                    </div>
                </div>
                <div className="my-2">
                    <label className="block text-sm font-medium mb-1">Meals to skip:</label>
                    <div className="flex gap-4">
                        {Object.keys(meals).map(meal => (
                            <label key={meal} className="flex items-center gap-1"><input type="checkbox" checked={meals[meal]} onChange={() => setMeals(prev => ({ ...prev, [meal]: !prev[meal] }))} />{meal}</label>
                        ))}
                    </div>
                </div>
                <button onClick={handleAddDates} className="mt-2 w-full bg-[#782953] text-white font-bold py-2 px-4 rounded-md hover:bg-opacity-90">Add to Schedule</button>
            </div>
            <button onClick={handleSaveChanges} disabled={isLoading} className="mt-4 w-full bg-[#782953] text-white font-bold py-2 px-4 rounded-md hover:bg-opacity-90 disabled:bg-gray-400">Save Schedule Changes</button>
            {status.message && <div className={`mt-4 p-3 rounded-md text-center ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>{status.message}</div>}
        </div>
    );
}

function ForcedRolesList() {
    const [forcedRoles, setForcedRoles] = useState([]);
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, "forcedRoles"), (snapshot) => {
            setForcedRoles(snapshot.docs.map(doc => ({ email: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    const handleRemoveForcedRole = async (email) => {
        if (window.confirm(`Are you sure you want to remove the forced role for ${email}?`)) {
            const batch = writeBatch(db);
            batch.delete(doc(db, "forcedRoles", email));
            batch.delete(doc(db, "admins", email));
            batch.delete(doc(db, "chefs", email));
            await batch.commit();
        }
    };

    return (
         <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953]">Manage Forced Roles</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white">
                    <thead className="bg-gray-50"><tr><th className="p-2 border-b">Email</th><th className="p-2 border-b">Assigned Role</th><th className="p-2 border-b">Actions</th></tr></thead>
                    <tbody>
                        {forcedRoles.map(({ email, userType }) => (
                            <tr key={email} className="hover:bg-gray-50">
                                <td className="p-2 border-b">{email}</td>
                                <td className="p-2 border-b">{ROLES[userType] || userType}</td>
                                <td className="p-2 border-b"><button onClick={() => handleRemoveForcedRole(email)} className="text-red-500 hover:text-red-700">Remove</button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SeedDataDownloader() {
    const [isGenerating, setIsGenerating] = useState(false);
    const [status, setStatus] = useState({ message: '', isError: false });

    const generateSeedScript = async () => {
        setIsGenerating(true);
        setStatus({ message: 'Generating seed script...', isError: false });

        try {
            // Fetch all data from production Firestore
            const seedData = {
                admins: {},
                chefs: {},
                forcedRoles: {},
                configuration: {},
                users: {}
            };

            // Fetch admins
            const adminsSnapshot = await getDocs(collection(db, 'admins'));
            adminsSnapshot.forEach(doc => {
                seedData.admins[doc.id] = doc.data();
            });

            // Fetch chefs
            const chefsSnapshot = await getDocs(collection(db, 'chefs'));
            chefsSnapshot.forEach(doc => {
                seedData.chefs[doc.id] = doc.data();
            });

            // Fetch forced roles
            const forcedRolesSnapshot = await getDocs(collection(db, 'forcedRoles'));
            forcedRolesSnapshot.forEach(doc => {
                seedData.forcedRoles[doc.id] = doc.data();
            });

            // Fetch configuration
            const configSnapshot = await getDocs(collection(db, 'configuration'));
            configSnapshot.forEach(doc => {
                seedData.configuration[doc.id] = doc.data();
            });

            // Fetch users with their subcollections (profile and selections)
            const usersSnapshot = await getDocs(collection(db, 'users'));
            for (const userDoc of usersSnapshot.docs) {
                const userId = userDoc.id;
                seedData.users[userId] = {};

                // Fetch profile subcollection
                const profileSnapshot = await getDocs(collection(db, `users/${userId}/profile`));
                if (!profileSnapshot.empty) {
                    seedData.users[userId].profile = {};
                    profileSnapshot.forEach(profileDoc => {
                        seedData.users[userId].profile[profileDoc.id] = profileDoc.data();
                    });
                }

                // Fetch selections subcollection
                const selectionsSnapshot = await getDocs(collection(db, `users/${userId}/selections`));
                if (!selectionsSnapshot.empty) {
                    seedData.users[userId].selections = {};
                    selectionsSnapshot.forEach(selectionDoc => {
                        seedData.users[userId].selections[selectionDoc.id] = selectionDoc.data();
                    });
                }
            }

            // Generate the JavaScript seed script
            const scriptContent = `#!/usr/bin/env node

/**
 * Seed script to populate Firebase Emulator with production data
 * Generated on: ${new Date().toISOString()}
 *
 * SAFETY WARNING: This script is designed to ONLY work with Firebase emulators!
 * It will refuse to run against production to prevent accidental data corruption.
 *
 * Prerequisites:
 * 1. Install firebase-admin: npm install firebase-admin
 * 2. Make sure Firebase emulators are running: firebase emulators:start
 * 3. Verify emulators are accessible at the expected ports
 * 4. Run this script: node seed-emulator-data.js
 */

const admin = require('firebase-admin');

// SAFETY CHECK: Ensure we're only targeting emulators
const REQUIRED_EMULATOR_HOST = 'localhost:8080';
const EXPECTED_PROJECT_ID = 'refectory-15528';

console.log('🔍 Firebase Emulator Seed Script Starting...');
console.log('📊 Generated on: ${new Date().toISOString()}');
console.log('');

// Check if emulator environment is properly set
if (!process.env.FIRESTORE_EMULATOR_HOST) {
  console.log('⚠️  FIRESTORE_EMULATOR_HOST not set. Setting to localhost:8080');
  process.env.FIRESTORE_EMULATOR_HOST = REQUIRED_EMULATOR_HOST;
} else {
  console.log('✅ FIRESTORE_EMULATOR_HOST detected:', process.env.FIRESTORE_EMULATOR_HOST);
}

// Verify we're pointing to emulator, not production
if (!process.env.FIRESTORE_EMULATOR_HOST.includes('localhost') && !process.env.FIRESTORE_EMULATOR_HOST.includes('127.0.0.1')) {
  console.error('❌ SAFETY ERROR: FIRESTORE_EMULATOR_HOST does not point to localhost!');
  console.error('   Current value:', process.env.FIRESTORE_EMULATOR_HOST);
  console.error('   This script will only run against local emulators for safety.');
  console.error('   Expected: localhost:8080 or 127.0.0.1:8080');
  process.exit(1);
}

// Test emulator connectivity before proceeding
async function testEmulatorConnection() {
  try {
    console.log('🔍 Testing emulator connectivity...');
    const http = require('http');

    return new Promise((resolve) => {
      const req = http.get(\`http://\${process.env.FIRESTORE_EMULATOR_HOST}\`, (res) => {
        console.log('✅ Firestore emulator is accessible');
        resolve(true);
      });

      req.on('error', (error) => {
        console.error('❌ Cannot connect to Firestore emulator:', error.message);
        console.error('   Make sure emulators are running: firebase emulators:start');
        resolve(false);
      });

      req.setTimeout(5000, () => {
        console.error('❌ Connection to Firestore emulator timed out');
        console.error('   Make sure emulators are running: firebase emulators:start');
        req.destroy();
        resolve(false);
      });
    });
  } catch (error) {
    console.error('❌ Error testing emulator connection:', error.message);
    return false;
  }
}

// Initialize Firebase Admin SDK for emulator ONLY
console.log('🔧 Initializing Firebase Admin SDK for emulator...');
admin.initializeApp({
  projectId: EXPECTED_PROJECT_ID
});

const db = admin.firestore();

// Production data snapshot
const seedData = ${JSON.stringify(seedData, null, 2)};

async function seedEmulator() {
  console.log('🚀 Starting emulator seeding process...');
  console.log('📋 Target: Firestore emulator at', process.env.FIRESTORE_EMULATOR_HOST);
  console.log('🆔 Project ID:', EXPECTED_PROJECT_ID);
  console.log('');

  // Test connection first
  const isConnected = await testEmulatorConnection();
  if (!isConnected) {
    console.error('❌ Cannot proceed - emulator is not accessible');
    process.exit(1);
  }

  // Ask for confirmation
  console.log('⚠️  CONFIRMATION REQUIRED:');
  console.log('   This will populate the LOCAL EMULATOR with production data.');
  console.log('   The emulator database will be cleared and repopulated.');
  console.log('');

  // In a real environment, you might want to add a prompt here
  // For now, we'll proceed automatically since this is emulator-only

  try {
    console.log('🗑️  Clearing existing emulator data...');
    // Clear existing data by deleting collections
    const collections = ['admins', 'chefs', 'forcedRoles', 'configuration', 'users'];
    for (const collectionName of collections) {
      await clearCollection(collectionName);
    }
    console.log('✅ Emulator data cleared');
    console.log('');

    const batch = db.batch();
    let operationCount = 0;
    const maxBatchSize = 500;

    // Helper function to commit batch when it gets too large
    const commitBatchIfNeeded = async () => {
      if (operationCount >= maxBatchSize) {
        console.log(\`📝 Committing batch of \${operationCount} operations...\`);
        await batch.commit();
        operationCount = 0;
      }
    };

    // Seed admins collection
    console.log('👑 Seeding admins collection...');
    for (const [docId, data] of Object.entries(seedData.admins)) {
      const docRef = db.collection('admins').doc(docId);
      batch.set(docRef, data);
      operationCount++;
      await commitBatchIfNeeded();
    }

    // Seed chefs collection
    console.log('👨‍🍳 Seeding chefs collection...');
    for (const [docId, data] of Object.entries(seedData.chefs)) {
      const docRef = db.collection('chefs').doc(docId);
      batch.set(docRef, data);
      operationCount++;
      await commitBatchIfNeeded();
    }

    // Seed forcedRoles collection
    console.log('🎭 Seeding forcedRoles collection...');
    for (const [docId, data] of Object.entries(seedData.forcedRoles)) {
      const docRef = db.collection('forcedRoles').doc(docId);
      batch.set(docRef, data);
      operationCount++;
      await commitBatchIfNeeded();
    }

    // Seed configuration collection
    console.log('⚙️  Seeding configuration collection...');
    for (const [docId, data] of Object.entries(seedData.configuration)) {
      const docRef = db.collection('configuration').doc(docId);
      batch.set(docRef, data);
      operationCount++;
      await commitBatchIfNeeded();
    }

    // Seed users collection with subcollections
    console.log('👥 Seeding users collection...');
    let userCount = 0;
    for (const [userId, userData] of Object.entries(seedData.users)) {
      userCount++;
      if (userCount % 10 === 0) {
        console.log(\`   Processing user \${userCount}/\${Object.keys(seedData.users).length}...\`);
      }

      // Seed profile subcollection
      if (userData.profile) {
        for (const [profileDocId, profileData] of Object.entries(userData.profile)) {
          const profileRef = db.collection('users').doc(userId).collection('profile').doc(profileDocId);
          batch.set(profileRef, profileData);
          operationCount++;
          await commitBatchIfNeeded();
        }
      }

      // Seed selections subcollection
      if (userData.selections) {
        for (const [selectionDocId, selectionData] of Object.entries(userData.selections)) {
          const selectionRef = db.collection('users').doc(userId).collection('selections').doc(selectionDocId);
          batch.set(selectionRef, selectionData);
          operationCount++;
          await commitBatchIfNeeded();
        }
      }
    }

    // Commit any remaining operations
    if (operationCount > 0) {
      console.log(\`📝 Committing final batch of \${operationCount} operations...\`);
      await batch.commit();
    }

    console.log('');
    console.log('🎉 Emulator seeding completed successfully!');
    console.log('📊 Seeded data summary:');
    console.log(\`   👑 Admins: \${Object.keys(seedData.admins).length}\`);
    console.log(\`   👨‍🍳 Chefs: \${Object.keys(seedData.chefs).length}\`);
    console.log(\`   🎭 Forced Roles: \${Object.keys(seedData.forcedRoles).length}\`);
    console.log(\`   ⚙️  Configuration docs: \${Object.keys(seedData.configuration).length}\`);
    console.log(\`   👥 Users: \${Object.keys(seedData.users).length}\`);
    console.log('');
    console.log('✅ Your Firebase emulator is now populated with production data!');
    console.log('🌐 Access the emulator UI at: http://localhost:4000');

  } catch (error) {
    console.error('❌ Error seeding emulator:', error);
    process.exit(1);
  }
}

// Helper function to clear a collection
async function clearCollection(collectionName) {
  const collectionRef = db.collection(collectionName);
  const snapshot = await collectionRef.get();

  if (snapshot.empty) {
    console.log(\`   \${collectionName}: already empty\`);
    return;
  }

  console.log(\`   \${collectionName}: deleting \${snapshot.size} documents...\`);
  const batch = db.batch();
  snapshot.docs.forEach(doc => batch.delete(doc.ref));
  await batch.commit();
}

// Run the seeding process
console.log('🔐 Running safety checks...');
seedEmulator().then(() => {
  console.log('🏁 Seeding process completed successfully.');
  process.exit(0);
}).catch(error => {
  console.error('💥 Fatal error:', error);
  process.exit(1);
});
`;

            // Create and download the file
            const blob = new Blob([scriptContent], { type: 'application/javascript' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `seed-emulator-data-${new Date().toISOString().split('T')[0]}.js`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            setStatus({
                message: `Seed script generated and downloaded successfully! Found ${Object.keys(seedData.users).length} users, ${Object.keys(seedData.admins).length} admins, and ${Object.keys(seedData.chefs).length} chefs.`,
                isError: false
            });

        } catch (error) {
            console.error('Error generating seed script:', error);
            setStatus({
                message: `Error generating seed script: ${error.message}`,
                isError: true
            });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Download Emulator Seed Script</h2>
            <p className="text-gray-600 mb-6">
                Generate and download a JavaScript script that will seed your Firebase emulator with current production data.
                This includes all users, admins, chefs, roles, and meal selections.
            </p>

            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <h3 className="font-semibold text-green-800 mb-2">🔒 Safety Features:</h3>
                <ul className="list-disc list-inside text-sm text-green-700 space-y-1">
                    <li>Script will <strong>ONLY</strong> run against local emulators (localhost)</li>
                    <li>Automatically refuses to connect to production</li>
                    <li>Tests emulator connectivity before proceeding</li>
                    <li>Clears emulator data before seeding to ensure clean state</li>
                </ul>
            </div>

            <div className="mb-4">
                <h3 className="font-semibold text-[#782953] mb-2">Instructions:</h3>
                <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1">
                    <li>Click the button below to generate and download the seed script</li>
                    <li>Make sure you have <code className="bg-gray-100 px-1 rounded">firebase-admin</code> installed: <code className="bg-gray-100 px-1 rounded">npm install firebase-admin</code></li>
                    <li>Start your Firebase emulators: <code className="bg-gray-100 px-1 rounded">firebase emulators:start</code></li>
                    <li>Verify emulators are running at <code className="bg-gray-100 px-1 rounded">http://localhost:4000</code></li>
                    <li>Run the downloaded script: <code className="bg-gray-100 px-1 rounded">node seed-emulator-data-YYYY-MM-DD.js</code></li>
                    <li>The script will safely populate your emulator with production data</li>
                </ol>
            </div>

            <button
                onClick={generateSeedScript}
                disabled={isGenerating}
                className="w-full bg-[#782953] text-white font-bold py-3 px-6 rounded-md hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
                {isGenerating ? (
                    <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Generating Script...
                    </>
                ) : (
                    <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Download Seed Script
                    </>
                )}
            </button>

            {status.message && (
                <div className={`mt-4 p-3 rounded-md text-sm ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {status.message}
                </div>
            )}
        </div>
    );
}

function SectionAccessManager() {
    const [sectionConfig, setSectionConfig] = useState({});
    const [status, setStatus] = useState({ message: '', isError: false });
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Available sections and user types
    const sections = [
        { id: 'meal-planner', name: 'Meal Planning', description: 'Weekly meal selection and dining hall features' },
        { id: 'community-service', name: 'Community Service', description: 'Community service hour tracking and reporting' },
        { id: 'chapel-attendance', name: 'Chapel Attendance', description: 'Chapel service attendance tracking (Admin only)' }
    ];

    const userTypes = [
        { id: 'board_paying_student', name: 'Board Paying Student' },
        { id: 'non_board_paying', name: 'Non-Board Paying Student' },
        { id: 'staff_or_faculty', name: 'Staff / Faculty' }
    ];

    // Load current configuration
    useEffect(() => {
        loadSectionConfig();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSectionConfig = async () => {
        setIsLoading(true);
        try {
            const configDoc = await getDoc(doc(db, 'configuration', 'sectionAccess'));
            if (configDoc.exists()) {
                setSectionConfig(configDoc.data());
            } else {
                // Default configuration - all sections available to all user types except chapel
                const defaultConfig = {};
                sections.forEach(section => {
                    defaultConfig[section.id] = {};
                    userTypes.forEach(userType => {
                        // Chapel attendance is admin-only by default
                        defaultConfig[section.id][userType.id] = section.id !== 'chapel-attendance';
                    });
                });
                setSectionConfig(defaultConfig);
            }
        } catch (error) {
            setStatus({ message: `Error loading configuration: ${error.message}`, isError: true });
        } finally {
            setIsLoading(false);
        }
    };

    const saveSectionConfig = async () => {
        setIsSaving(true);
        setStatus({ message: 'Saving configuration...', isError: false });
        try {
            await setDoc(doc(db, 'configuration', 'sectionAccess'), sectionConfig);
            setStatus({ message: 'Configuration saved successfully!', isError: false });
        } catch (error) {
            setStatus({ message: `Error saving configuration: ${error.message}`, isError: true });
        } finally {
            setIsSaving(false);
        }
    };

    const toggleAccess = (sectionId, userTypeId) => {
        setSectionConfig(prev => ({
            ...prev,
            [sectionId]: {
                ...prev[sectionId],
                [userTypeId]: !prev[sectionId]?.[userTypeId]
            }
        }));
    };

    if (isLoading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Section Access Management</h2>
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#782953]"></div>
                    <span className="ml-2">Loading configuration...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-2xl font-semibold mb-4 text-[#782953] border-b pb-2">Section Access Management</h2>
            <p className="text-gray-600 mb-6">
                Control which sections of the application are visible to different user types.
                Changes take effect immediately for all users.
            </p>

            <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="bg-gray-50">
                            <th className="text-left p-3 border-b font-semibold text-gray-700">Section</th>
                            {userTypes.map(userType => (
                                <th key={userType.id} className="text-center p-3 border-b font-semibold text-gray-700">
                                    {userType.name}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map(section => (
                            <tr key={section.id} className="hover:bg-gray-50">
                                <td className="p-3 border-b">
                                    <div>
                                        <div className="font-medium text-gray-900">{section.name}</div>
                                        <div className="text-sm text-gray-500">{section.description}</div>
                                    </div>
                                </td>
                                {userTypes.map(userType => (
                                    <td key={userType.id} className="p-3 border-b text-center">
                                        <button
                                            onClick={() => toggleAccess(section.id, userType.id)}
                                            className={`w-12 h-6 rounded-full relative transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#782953] focus:ring-opacity-50 ${
                                                sectionConfig[section.id]?.[userType.id]
                                                    ? 'bg-[#782953]'
                                                    : 'bg-gray-300'
                                            }`}
                                        >
                                            <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-transform duration-200 ${
                                                sectionConfig[section.id]?.[userType.id]
                                                    ? 'transform translate-x-6'
                                                    : 'transform translate-x-1'
                                            }`} />
                                        </button>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="flex justify-between items-center mt-6">
                <button
                    onClick={loadSectionConfig}
                    disabled={isSaving}
                    className="px-4 py-2 text-[#782953] border border-[#782953] rounded-md hover:bg-[#782953] hover:text-white transition-colors disabled:opacity-50"
                >
                    Reset Changes
                </button>
                <button
                    onClick={saveSectionConfig}
                    disabled={isSaving}
                    className="px-6 py-2 bg-[#782953] text-white rounded-md hover:bg-opacity-90 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {isSaving ? (
                        <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Saving...
                        </>
                    ) : (
                        'Save Configuration'
                    )}
                </button>
            </div>

            {status.message && (
                <div className={`mt-4 p-3 rounded-md text-sm ${status.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`}>
                    {status.message}
                </div>
            )}
        </div>
    );
}

function SectionNotAvailable({ currentApp, userType, isSectionAccessible, setCurrentApp }) {
    // Find the first available section for this user type
    const availableSections = ["meal-planner", "community-service", "chapel-attendance"]
        .filter(section => isSectionAccessible(section, userType));

    useEffect(() => {
        // If current section is not accessible, redirect to first available section
        if (availableSections.length > 0 && !isSectionAccessible(currentApp, userType)) {
            setCurrentApp(availableSections[0]);
        }
    }, [availableSections, currentApp, isSectionAccessible, setCurrentApp, userType]);

    if (availableSections.length === 0) {
        return (
            <div className="container mx-auto text-center py-16">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md mx-auto">
                    <div className="text-6xl mb-4">🚫</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">No Sections Available</h2>
                    <p className="text-gray-600 mb-6">
                        No sections of the application are currently available for your user type ({userType}).
                    </p>
                    <p className="text-sm text-gray-500">
                        Please contact an administrator if you believe this is an error.
                    </p>
                </div>
            </div>
        );
    }

    const sectionNames = {
        "meal-planner": "Meal Planning",
        "community-service": "Community Service",
        "chapel-attendance": "Chapel Attendance"
    };

    return (
        <div className="container mx-auto text-center py-16">
            <div className="bg-white p-8 rounded-lg shadow-md max-w-md mx-auto">
                <div className="text-6xl mb-4">⚠️</div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Section Not Available</h2>
                <p className="text-gray-600 mb-6">
                    The "{sectionNames[currentApp] || currentApp}" section is not available for your user type ({userType}).
                </p>
                <div className="space-y-2">
                    <p className="text-sm text-gray-700 font-semibold">Available sections:</p>
                    {availableSections.map(section => (
                        <button
                            key={section}
                            onClick={() => setCurrentApp(section)}
                            className="block w-full bg-[#782953] text-white py-2 px-4 rounded-md hover:bg-opacity-90 transition-colors"
                        >
                            {sectionNames[section] || section}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Global Save Reminder Component - Fixed Position
function GlobalSaveReminder() {
    const [dirtyComponents, setDirtyComponents] = useState(new Map());
    const [isVisible, setIsVisible] = useState(false);

    // Listen for global save reminder events
    useEffect(() => {
        const handleSaveReminderUpdate = (event) => {
            const { componentId, isDirty, saveFunction } = event.detail;

            setDirtyComponents(prev => {
                const newMap = new Map(prev);
                if (isDirty && saveFunction) {
                    newMap.set(componentId, saveFunction);
                } else {
                    newMap.delete(componentId);
                }
                return newMap;
            });
        };

        const handleCheckDirtyComponents = (event) => {
            if (event.detail && event.detail.callback) {
                event.detail.callback(dirtyComponents.size > 0);
            }
        };

        const handleSaveAllChanges = () => {
            handleSaveAll();
        };

        const handleClearAllChanges = () => {
            setDirtyComponents(new Map());
        };

        window.addEventListener('saveReminderUpdate', handleSaveReminderUpdate);
        window.addEventListener('checkDirtyComponents', handleCheckDirtyComponents);
        window.addEventListener('saveAllChanges', handleSaveAllChanges);
        window.addEventListener('clearAllChanges', handleClearAllChanges);

        return () => {
            window.removeEventListener('saveReminderUpdate', handleSaveReminderUpdate);
            window.removeEventListener('checkDirtyComponents', handleCheckDirtyComponents);
            window.removeEventListener('saveAllChanges', handleSaveAllChanges);
            window.removeEventListener('clearAllChanges', handleClearAllChanges);
        };
    }, [dirtyComponents]);

    // Update visibility based on dirty components
    useEffect(() => {
        setIsVisible(dirtyComponents.size > 0);

        // Add top padding to body when save reminder is visible
        if (dirtyComponents.size > 0) {
            document.body.style.paddingTop = '70px';
        } else {
            document.body.style.paddingTop = '0px';
        }

        // Cleanup on unmount
        return () => {
            document.body.style.paddingTop = '0px';
        };
    }, [dirtyComponents]);

    // Global beforeunload protection for page refresh/close
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (dirtyComponents.size > 0) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [dirtyComponents]);

    const handleSaveAll = () => {
        // Call save function for all dirty components
        for (const saveFunction of dirtyComponents.values()) {
            if (saveFunction) {
                saveFunction();
            }
        }
    };

    if (!isVisible) return null;

    return (
        <div className="fixed top-0 left-0 right-0 z-50 bg-yellow-100 border-b-4 border-yellow-500 text-yellow-800 px-4 py-3 shadow-lg">
            <div className="container mx-auto flex flex-col sm:flex-row items-center justify-between">
                <div className="flex items-center mb-2 sm:mb-0">
                    <span className="text-yellow-600 mr-2">⚠️</span>
                    <p className="font-bold text-sm sm:text-base">
                        You have unsaved changes!
                    </p>
                </div>
                <button
                    onClick={handleSaveAll}
                    className="bg-[#782953] text-white font-bold py-2 px-4 sm:px-6 rounded-lg shadow-lg hover:bg-opacity-90 transition-colors text-sm sm:text-base"
                >
                    Save All Changes
                </button>
            </div>
        </div>
    );
}
